import {
  CalendarCheck,
  CircleSlash,
  FileWarning,
  Loader,
  MailCheck,
  Send,
  Undo2,
  type LucideIcon,
} from 'lucide-react';

/**
 * Status oficiais da solicitação. Espelha o enum `StatusSolicitacao` do
 * backend — qualquer alteração precisa acontecer nos dois lados.
 */
export const STATUS = [
  'ENVIADO',
  'LIDO_PELA_REGULACAO',
  'PENDENCIA_DOCUMENTACAO',
  'EM_PROCESSO',
  'AGENDADO',
  'DEVOLVIDO',
  'CANCELADO',
] as const;

export type StatusSolicitacao = (typeof STATUS)[number];

/** Espelho de `STATUS_ABERTOS` do backend: o processo ainda espera decisão. */
export const STATUS_ABERTOS: readonly StatusSolicitacao[] = [
  'ENVIADO',
  'LIDO_PELA_REGULACAO',
  'PENDENCIA_DOCUMENTACAO',
  'EM_PROCESSO',
];

/**
 * O valor que representa "em aberto" no filtro de status da listagem.
 *
 * O backend aceita `status=A,B` na query, então um recorte de vários status
 * cabe num valor só de `<select>` — e é isso que permite a um cartão do painel
 * abrir a lista exatamente no número que ele mostra, com o filtro visível na
 * tela em vez de aplicado em silêncio.
 */
export const VALOR_STATUS_ABERTOS = STATUS_ABERTOS.join(',');

export interface DefinicaoStatus {
  /** Texto exibido ao usuário. Sempre visível — a cor nunca comunica sozinha. */
  rotulo: string;
  /** Explicação usada em tooltip e leitores de tela. */
  descricao: string;
  icone: LucideIcon;
  /** Classes do badge: fundo, texto e borda derivados dos tokens do tema. */
  classes: string;
}

export const DEFINICOES_STATUS: Record<StatusSolicitacao, DefinicaoStatus> = {
  ENVIADO: {
    rotulo: 'Enviado',
    descricao: 'Solicitação registrada pela UBS e aguardando análise da Regulação.',
    icone: Send,
    classes: 'bg-status-enviado-fundo text-status-enviado-texto border-status-enviado-borda',
  },
  LIDO_PELA_REGULACAO: {
    rotulo: 'Lido pela Regulação',
    descricao: 'A Regulação já visualizou a solicitação e ela está em análise.',
    icone: MailCheck,
    classes: 'bg-status-lido-fundo text-status-lido-texto border-status-lido-borda',
  },
  PENDENCIA_DOCUMENTACAO: {
    rotulo: 'Pendência de documentação',
    descricao: 'A Regulação identificou documentação faltante ou que precisa de correção.',
    icone: FileWarning,
    classes: 'bg-status-pendencia-fundo text-status-pendencia-texto border-status-pendencia-borda',
  },
  EM_PROCESSO: {
    rotulo: 'Em processo',
    descricao: 'A documentação física chegou à Regulação e a solicitação está sendo processada.',
    icone: Loader,
    classes: 'bg-status-processo-fundo text-status-processo-texto border-status-processo-borda',
  },
  AGENDADO: {
    rotulo: 'Agendado',
    descricao: 'Agendamento confirmado. O paciente foi notificado.',
    icone: CalendarCheck,
    classes: 'bg-status-agendado-fundo text-status-agendado-texto border-status-agendado-borda',
  },
  DEVOLVIDO: {
    rotulo: 'Devolvido',
    descricao: 'Solicitação devolvida à UBS de origem, com justificativa.',
    icone: Undo2,
    classes: 'bg-status-devolvido-fundo text-status-devolvido-texto border-status-devolvido-borda',
  },
  CANCELADO: {
    rotulo: 'Cancelado',
    descricao: 'O paciente não vai comparecer. A vaga foi liberada.',
    icone: CircleSlash,
    classes: 'bg-status-cancelado-fundo text-status-cancelado-texto border-status-cancelado-borda',
  },
};

export function obterDefinicaoStatus(status: StatusSolicitacao): DefinicaoStatus {
  return DEFINICOES_STATUS[status];
}

/** Prioridades da solicitação — espelha o enum `Prioridade` do backend. */
export const PRIORIDADES = ['ELETIVA', 'PRIORITARIA', 'URGENTE'] as const;

export type Prioridade = (typeof PRIORIDADES)[number];

export const ROTULOS_PRIORIDADE: Record<Prioridade, string> = {
  ELETIVA: 'Eletiva',
  PRIORITARIA: 'Prioritária',
  URGENTE: 'Urgente',
};

/** Explicação de cada nível, exibida no formulário da UBS. */
export const DESCRICOES_PRIORIDADE: Record<Prioridade, string> = {
  ELETIVA: 'Sem urgência clínica; segue a ordem da fila.',
  PRIORITARIA: 'Necessita atendimento em prazo menor que o habitual.',
  URGENTE: 'Risco de agravamento; deve ser analisada com precedência.',
};

/**
 * Classes do badge de prioridade. Combinadas sempre com o rótulo textual —
 * a cor sozinha não comunica.
 */
export const CLASSES_PRIORIDADE: Record<Prioridade, string> = {
  ELETIVA: 'bg-silenciado text-silenciado-texto border-borda',
  PRIORITARIA:
    'bg-status-pendencia-fundo text-status-pendencia-texto border-status-pendencia-borda',
  URGENTE: 'bg-status-devolvido-fundo text-status-devolvido-texto border-status-devolvido-borda',
};

/** Tipos de solicitação — espelha o enum `TipoSolicitacao` do backend. */
export const TIPOS_SOLICITACAO = ['CONSULTA', 'EXAME', 'PROCEDIMENTO'] as const;

export type TipoSolicitacao = (typeof TIPOS_SOLICITACAO)[number];

export const ROTULOS_TIPO: Record<TipoSolicitacao, string> = {
  CONSULTA: 'Consulta',
  EXAME: 'Exame',
  PROCEDIMENTO: 'Procedimento',
};
